import {UserAccessStatus} from './user-access.enum';

/**
 * ### IUserAccess
 * Interface representing a user's access information across stores and features.
 *
 * This interface encapsulates all relevant data related to a user's access rights within
 * the system, including feature flags, store-level permissions, and administrative roles.
 *
 * @interface IUserAccess
 * @description This interface holds all the data associated with a user's access permissions,
 *              such as the stores they can access, their feature flags, administrative rights, and status.
 *              It is used for managing and verifying a user's access rights within various parts of the application.
 *
 * @example
 * const userAccess: IUserAccess = {
 *   features: 123,
 *   status: 'ACTIVE',
 *   storeList: ['store_1', 'store_2'],
 *   stores: { 'store_1': 1, 'store_2': 2 },
 * };
 */
export interface IUserAccess {
  /**
   * Numeric representation of the features the user has access to.
   * This typically encodes permissions using bitwise flags.
   *
   * @type {number}
   * @example 123
   */
  features: number;

  /**
   * The current access status of the user.
   *
   * @type {UserAccessStatus}
   * @example 'ACTIVE'
   */
  status: UserAccessStatus;

  /**
   * Array of store IDs the user has access to.
   *
   * @type {string[]}
   * @example ['store_1', 'store_2']
   */
  storeList: string[];

  /**
   * Mapping of store IDs to access levels (numeric values).
   * Used to differentiate access types per store.
   *
   * @type {{ [storeId: string]: number }}
   * @example { 'store_1': 1, 'store_2': 2 }
   */
  stores: Record<string, number>;

  /**
   * (Optional) List of store IDs where the user has admin access.
   * Enables elevated permissions within these stores.
   *
   * @type {string[]}
   * @optional
   * @example ['store_1']
   */
  adminAccessStores?: string[];

  /**
   * (Optional) Date when the user's access was created.
   * Useful for auditing and historical tracking.
   *
   * @type {Date}
   * @optional
   * @example new Date('2025-01-01')
   */
  created?: Date;

  /**
   * (Optional) Whether this access object was created in a server-side context.
   * Can affect how permissions are interpreted or synced.
   *
   * @type {boolean}
   * @optional
   * @example true
   */
  isServer?: boolean;

  /**
   * (Optional) Timestamp of when the last password reset link was sent.
   * Helps track security-related actions.
   *
   * @type {Date}
   * @optional
   * @example new Date('2025-04-15')
   */
  lastPasswordLinkTimestamp?: Date;

  /**
   * (Optional) Mapping of store IDs to arrays of boolean flags.
   * Represents granular, feature-specific flags at the store level.
   *
   * @type {{ [storeId: string]: boolean[] }}
   * @optional
   * @example { 'store_1': [true, false], 'store_2': [false, true] }
   */
  storeFlags?: Record<string, boolean[]>;

  /**
   * (Optional) ID of the user associated with this access object.
   *
   * @type {string}
   * @optional
   * @example 'user_123abc'
   */
  userId?: string;
}
